import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  Clock,
  ArrowRight,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  MapPin,
  Shield,
  CheckCircle,
} from "lucide-react";
import logo from "../../Assets/Logos/logoWhite.webp";
import servicesData from "../../context/Services.json";

const UserFooter = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // Get only the main service categories (first 4)
  const mainServiceCategories = servicesData.services.slice(0, 4);

  const footerData = {
    company: {
      description:
        "Leading provider of GIS, IT, Consulting, and Printing solutions with precision, technology, and innovation.",
      contact: {
        phone: "+91 876 320 9815",
        email: "mtspvtltd2012@gmail.com",
        address: "N - 5/111, Ground Floor, Biju Pattnaik College Road,Jaydev Vihar, Bhubaneswar, Odisha",
      },
      hours: "Mon - Fri: 9:00 AM - 6:00 PM",
    },
    quickLinks: [
      { name: "Home", path: "/" },
      { name: "About Us", path: "/about" },
      { name: "Our Team", path: "/team" },
      { name: "Projects", path: "/projects" },
      { name: "Contact", path: "/contact" },
    ],
    services: mainServiceCategories, // Now shows main categories only
    socialLinks: [
      { name: "LinkedIn", icon: <Linkedin className="w-4 h-4" />, href: "#" },
      { name: "Twitter", icon: <Twitter className="w-4 h-4" />, href: "#" },
      { name: "Facebook", icon: <Facebook className="w-4 h-4" />, href: "#" },
      { name: "Instagram", icon: <Instagram className="w-4 h-4" />, href: "#" },
      { name: "YouTube", icon: <Youtube className="w-4 h-4" />, href: "#" },
    ],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  // Function to handle service category click
  const handleServiceCategoryClick = (categoryId) => {
    navigate(`/services/${categoryId}`);
    window.scrollTo(0, 0);
  };

  return (
    <footer className="tracking-wide relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-72 h-72 bg-brandBlue rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-brandGreen rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 py-6 lg:py-12"
        >
          {/* Company Info */}
          <motion.div variants={itemVariants} className="lg:col-span-4 md:col-span-2">
            <div className="mb-8">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="inline-block mb-6 cursor-pointer"
                onClick={() => navigate("/")}
              >
                <img src={logo} alt="Maptech Logo" className="h-14 w-auto lg:h-20" />
              </motion.div>
              <p className="text-gray-300 leading-relaxed mb-6 text-base font-normal">
                {footerData.company.description}
              </p>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-gray-300 group">
                <div className="w-10 h-10 rounded-xl bg-brandBlue/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-brandBlue" />
                </div>
                <span>{footerData.company.contact.phone}</span>
              </div>

              <div className="flex items-center space-x-3 text-gray-300 group">
                <div className="w-10 h-10 rounded-xl bg-brandBlue/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-brandBlue" />
                </div>
                <span>{footerData.company.contact.email}</span>
              </div>

              <div className="flex items-center space-x-3 text-gray-300 group">
                <div className="w-10 h-10 rounded-xl bg-brandBlue/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-brandBlue" />
                </div>
                <span>{footerData.company.contact.address}</span>
              </div>

              <div className="flex items-center space-x-3 text-gray-300 group">
                <div className="w-10 h-10 rounded-xl bg-brandBlue/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-brandBlue" />
                </div>
                <span>{footerData.company.hours}</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants} className="lg:col-span-2 md:col-span-1">
            <h3 className="text-lg lg:text-xl font-medium mb-6 flex items-center">
              <span className="w-2 h-2 bg-brandBlue rounded-full mr-2"></span>Quick Links
            </h3>
            <ul className="space-y-3">
              {footerData.quickLinks.map((link, index) => (
                <li key={index}>
                  <motion.div
                    onClick={() => navigate(link.path)}
                    className="flex items-center space-x-3 text-gray-300 hover:text-white cursor-pointer transition-all group py-2"
                    whileHover={{ x: 8 }}
                  >
                    <ArrowRight className="w-4 h-4 text-brandBlue opacity-0 group-hover:opacity-100 transition-all" />
                    <span>{link.name}</span>
                  </motion.div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Service Categories - Now shows main 4 categories */}
          <motion.div variants={itemVariants} className="lg:col-span-3 md:col-span-1">
            <h3 className="text-lg lg:text-xl font-medium mb-6 flex items-center">
              <span className="w-2 h-2 bg-brandGreen rounded-full mr-2"></span>Our Services
            </h3>
            <div className="space-y-4">
              {footerData.services.map((category, index) => (
                <motion.div
                  key={category.categoryId}
                  onClick={() => handleServiceCategoryClick(category.categoryId)}
                  className="flex items-center space-x-3 text-gray-300 hover:text-white cursor-pointer transition-all group p-3 rounded-xl hover:bg-white/5"
                  whileHover={{ x: 8 }}
                  title={`Explore ${category.category}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-brandGreen/20 flex items-center justify-center flex-shrink-0 group-hover:bg-brandGreen/30 transition-colors">
                    <img 
                      src={category.icon} 
                      alt={category.category}
                      className="w-5 h-5 object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm group-hover:text-brandGreen transition-colors truncate">
                      {category.category}
                    </h4>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                      {category.description.substring(0, 60)}...
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-brandGreen opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Social / Trust Section */}
          <motion.div variants={itemVariants} className="lg:col-span-3 md:col-span-2">
            <div className="mb-8">
              <h3 className="text-lg lg:text-xl font-medium mb-6">Connect With Us</h3>
              <div className="flex flex-wrap gap-3">
                {footerData.socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 group"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                <Shield className="w-5 h-5 text-brandGreen mr-2" />
                Certified & Trusted
              </h4>
              <div className="space-y-3">
                {[
                  "ISO 9001 Certified",
                  "24/7 Customer Support",
                  "Data Security Compliant",
                ].map((text, i) => (
                  <div key={i} className="flex items-center space-x-3 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-slate-800 px-4">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-3 h-3 bg-gradient-to-r from-brandBlue to-brandGreen rounded-full"
              />
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="py-8"
        >
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <div className="text-gray-400 text-sm text-center lg:text-left">
              © {currentYear} Maptech Solutions Pvt Ltd. All rights reserved.
            </div>

            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="w-2 h-2 bg-green-500 rounded-full"
              />
              <span>Secure & Trusted</span>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default UserFooter;